import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import ReactMarkdown from "react-markdown";
import Button from "@/components/actions/Button";
import {
  CalendarIcon,
  LocationMarkerIcon,
  ArrowUpIcon,
} from "@heroicons/react/outline";
import { Nav } from "@/components/navigation/Navbar";
import Toast from "@/components/overlays/Toast";
import qs from "qs";
import apiClient from "@/utils/apiClient";
import format from "date-fns/format";
import { es } from "date-fns/locale";
import { add } from "date-fns";
import { createCalendarEvent } from "@/utils/createEvent";
import RelatedContent from "@/components/movies/RelatedContent";
import { AddToCalendarButton } from "add-to-calendar-button-react";

import * as Text from "@/components/text";
import classNames from "classnames";

export default function Index({ event }) {
  const router = useRouter();
  const navRef = useRef(null);

  const [show, setShow] = React.useState(false); // toast
  let [currentPage, setCurrentPage] = React.useState("");

  useEffect(() => {
    setCurrentPage(window.location.href);
  }, []);

  const tags = [
    "Conflictos sociales",
    "Culturas y pueblos ancestrales",
    "Ecologías del cine",
    "Justicia",
    "Latinoamérica",
    "Medio ambiente",
  ];

  return (
    <div>
      <div ref={navRef} className="bg-white amb-container">
        <Nav theme="light" />
      </div>

      <div className="sticky top-0 z-50 ">
        <div className="py-3 bg-neutral-50 ">
          <div className="amb-container">
            <ul className="flex items-center gap-5">
              <li className="flex space-x-2">
                <Text.Link
                  label="← Atrás"
                  onClick={() => router.back()}
                  size="md"
                  className="items-center flex-none hidden align-middle text-neutral-1000 md:flex"
                ></Text.Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label="Inicio"
                  ></Text.Link>
                </Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/calendario">
                  <Text.Link
                    className="text-neutral-1000"
                    size="md"
                    label="Calendario"
                  ></Text.Link>
                </Link>
              </li>
              <li>
                <Text.Body
                  className="overflow-hidden font-light break-normal line-clamp-1 text-ellipsis text-neutral-500"
                  size="lg"
                >
                  {event.attributes.event_name}
                </Text.Body>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full">
        <img
          src={event?.attributes?.event_image?.data?.attributes?.url}
          className="relative h-[320px] w-full object-cover lg:h-[526px] "
          alt=""
        />
      </div>

      <div className="amb-container">
        <div className="grid-cols-4 gap-16 mt-8 lg:grid">
          <div className="col-span-3 ">
            <div className="space-y-3">
              <div className="border-b-2 border-b-black">
                <span className="p-1 px-3 text-white uppercase bg-black">
                  {event.attributes.event_type}
                </span>
                <span> por Ambulante </span>
              </div>

              <Text.Display
                as="h1"
                className="hidden md:line-clamp-3 "
                size="sm"
              >
                {event.attributes.event_name}
              </Text.Display>
              <Text.Headline
                fontWeight="bold"
                as="h1"
                className="line-clamp-3 md:hidden"
                size="sm"
              >
                {event.attributes.event_name}
              </Text.Headline>

              <div className="flex flex-wrap items-center space-y-2 lg:flex-nowrap lg:space-y-0 lg:divide-x-2">
                <div className="border-r-2 lg:border-r-0">
                  <Text.Headline
                    as="h3"
                    fontWeight="regular"
                    className="hidden pr-2 capitalize leading-24 md:line-clamp-3 "
                    size="sm"
                  >
                    {format(new Date(event.attributes.date_time), "EE d LLLL", {
                      locale: es,
                    })}
                  </Text.Headline>
                  <Text.Title
                    as="h3"
                    fontWeight="regular"
                    className="pr-2 capitalize leading-24 line-clamp-3 md:hidden "
                    size="lg"
                  >
                    {format(new Date(event.attributes.date_time), "EE d LLLL", {
                      locale: es,
                    })}
                  </Text.Title>
                </div>
                <div className="flex flex-col">
                  <Text.Headline
                    as="p"
                    fontWeight="regular"
                    className="hidden px-2 pl-4 border-r-2 leading-24 text-neutral-900 md:flex lg:border-r-0 "
                    size="sm"
                  >
                    {format(new Date(event.attributes.date_time), "k:mm")} hrs a
                  </Text.Headline>
                  <Text.Headline
                    as="p"
                    fontWeight="regular"
                    className="hidden px-2 pl-4 border-r-2 leading-24 text-neutral-900 md:flex lg:border-r-0 "
                    size="sm"
                  >
                    {format(
                      add(new Date(event.attributes.date_time), {
                        minutes: event.attributes.event_duration,
                      }),
                      "k:mm"
                    )}&nbsp;
                    hrs
                  </Text.Headline>
                  <Text.Title
                    as="p"
                    className="px-2 pl-4 font-normal border-r-2 leading-24 line-clamp-3 md:hidden lg:border-r-0 "
                    size="lg"
                  >
                    <span className="flex-grow">
                      {format(new Date(event.attributes.date_time), "k:mm")} hrs
                      a
                    </span>
                    <br />
                    {format(
                      add(new Date(event.attributes.date_time), {
                        minutes: event.attributes.event_duration,
                      }),
                      "k:mm"
                    )}
                    hrs
                  </Text.Title>
                </div>
                {event?.attributes?.sede?.data?.attributes?.name ? (
                  <div className=" max-w-[450px] space-y-2 lg:space-y-0 lg:px-2  ">
                    <Text.Body
                      as="p"
                      size="xl"
                      className="col-span-2 ml-2 font-semibold "
                    >
                      <LocationMarkerIcon className="inline w-5 h-5 " />&nbsp;
                      {event?.attributes?.sede?.data?.attributes?.name || ""}
                    </Text.Body>
                    <div>
                      <Link
                        href={
                          event?.attributes?.sede?.data?.attributes
                            ?.venue_map_url || ""
                        }
                      >
                        <Text.Body
                          as="p"
                          size="lg"
                          className="col-span-2 underline md:pl-4"
                        >
                          {event?.attributes?.sede?.data?.attributes
                            ?.venue_address || ""}
                        </Text.Body>
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-10 lg:hidden">
              <div className="p-3 text-center bg-gray-200 divide-y-2 lg:divide-neutral-200 lg:text-left">
                {event?.attributes?.sede?.data?.attributes?.type ? (
                  <div className="py-2">
                    <Text.Overline size="lg" className="text-neutral-600">
                      Modalidad
                    </Text.Overline>
                    <Text.Body as="p">
                      {event?.attributes?.sede?.data?.attributes?.type || ""}
                    </Text.Body>
                  </div>
                ) : null}
                {event.attributes.event_duration ? (
                  <div className="py-2">
                    <Text.Overline size="lg" className="text-neutral-600">
                      Duración
                    </Text.Overline>
                    <Text.Body as="p">
                      {event.attributes.event_duration} mins
                    </Text.Body>
                  </div>
                ) : null}
                <div className="py-2">
                  <Text.Overline size="lg" className="text-neutral-600">
                    Entrada
                  </Text.Overline>
                  <Text.Body as="p">
                    {event.attributes.event_admission_fee
                      ? "Entrada con costo"
                      : "Entrada gratuita"}
                  </Text.Body>
                </div>
              </div>
            </div>

            <ReactMarkdown className="font-body prose mt-10 prose-h2:uppercase lg:max-w-[100ch]">
              {event.attributes.event_description}
            </ReactMarkdown>
          </div>

          <div className="space-y-10 mt-14 lg:mt-0">
            <div className="gap-3 space-y-0 text-center md:flex lg:block lg:space-y-6">
              <div className="flex items-center flex-1 lg:col-span-2">
                {event.attributes.signup_until ? (
                  <div className="m-auto">
                    <Text.Overline as="p" className="text-center">
                      FECHA LÍMITE de INSCRIPCIÓN
                    </Text.Overline>

                    <p className="font-bold text-center capitalize">
                      {format(
                        new Date(event.attributes.signup_until),
                        "EEEE d LLLL",
                        {
                          locale: es,
                        }
                      )}
                    </p>
                  </div>
                ) : null}
              </div>

              <div className="flex-1 py-3 space-y-3">
                {event.attributes.signup_until ? (
                  <div className="text-center md:text-left ">
                    <Link
                      href={event?.attributes?.signup_url || ""}
                      passHref
                      target="_blank"
                    >
                      <Button
                        color="secondary-light"
                        size="md"
                        className="w-full lg:mt-5"
                        label="Inscribirme"
                      ></Button>
                    </Link>
                  </div>
                ) : null}

                <div className="flex items-center justify-center text-center align-middle">
                  <AddToCalendarButton
                    name={event.attributes.event_name || ""}
                    options={["Apple", "Google"]}
                    buttonStyle="flat"
                    location={
                      event.attributes?.sede?.data?.attributes?.venue_address ||
                      ""
                    }
                    startDate={format(
                      new Date(event.attributes.date_time),
                      "yyyy-MM-dd"
                    )}
                    endDate={format(
                      new Date(event.attributes.date_time),
                      "yyyy-MM-dd"
                    )}
                    startTime={format(
                      new Date(event.attributes.date_time),
                      "HH:mm"
                    )}
                    endTime={
                      event.attributes.event_duration
                        ? format(
                          add(new Date(event.attributes.date_time), {
                            minutes: event.attributes.event_duration,
                          }),
                          "HH:mm"
                        )
                        : null
                    }
                    label="Agendar"
                    language="es"
                    size="5"
                    timeZone="America/Los_Angeles"
                  ></AddToCalendarButton>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="p-3 text-center bg-gray-200 lg:divide-neutral-200 lg:text-left">
                {event?.attributes?.sede?.data?.attributes?.type ? (
                  <div className="py-2">
                    <Text.Overline size="lg" className="text-neutral-600">
                      Modalidad
                    </Text.Overline>
                    <Text.Body as="p">
                      {event?.attributes?.sede?.data?.attributes?.type || ""}
                    </Text.Body>
                  </div>
                ) : null}
                {event.attributes.event_duration ? (
                  <div className="py-2">
                    <Text.Overline size="lg" className="text-neutral-600">
                      &nbsp;
                      DURACIÓN
                    </Text.Overline>
                    <Text.Body as="p">
                      {event.attributes.event_duration} mins
                    </Text.Body>
                  </div>
                ) : null}

                <div className="py-2">
                  <Text.Overline size="lg" className="text-neutral-600">
                    Entrada
                  </Text.Overline>
                  <Text.Body as="p">
                    {event.attributes.event_admission_fee
                      ? "Entrada con costo"
                      : "Entrada gratuita"}
                  </Text.Body>
                </div>
              </div>
            </div>

            {event.attributes.temas.data.length > 0 ? (
              <div>
                <Text.Overline size="md" className="text-neutral-600">
                  Temáticas
                </Text.Overline>
                <ul className="flex flex-wrap mt-2 gap-x-2 gap-y-2">
                  {event.attributes.temas.data.map((topic, i) => (
                    <li
                      key={topic.id}
                      className="flex items-center flex-none px-3 rounded-full bg-neutral-100"
                    >
                      <Text.Body className="text-neutral-900" size="lg">
                        &nbsp;
                        {topic.attributes.name}&nbsp;
                      </Text.Body>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {event.attributes.iniciativas.data.length > 0 ? (
              <div>
                <Text.Overline size="md" className="text-neutral-600">
                  Programas
                </Text.Overline>
                <ul className="mt-2 space-y-2 ">
                  {event.attributes.iniciativas.data.map((initiative, i) => (
                    <li key={initiative.id} className="px-3 ">
                      <div
                        className={classNames(
                          {
                            "ambulante presenta": "bg-presenta",
                            ediciones: "bg-ediciones",
                            "gira ambulante": "bg-gira",
                            "mas-alla": "bg-mas-alla",
                          }[initiative.attributes.name.toLowerCase()],
                          "inline-block h-3 w-3 rounded-full"
                        )}
                      ></div>
                      <Text.Body
                        className="inline-block ml-2 border-b-2 border-neutral-300 text-neutral-900"
                        size="lg"
                      >
                        &nbsp;
                        {initiative.attributes.name}&nbsp;
                      </Text.Body>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        <div className="my-10 text-center amb-container lg:hidden">
          <Text.Link
            onClick={() =>
              navRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
              })
            }
            label="Volver arriba"
            trailingIcon={<ArrowUpIcon />}
            size="lg"
            className="m-auto text-center"
          >
            &nbsp;
          </Text.Link>
        </div>

        <div className="my-5">
          <RelatedContent
            movieId={event?.attributes?.peliculas?.data[0]?.id}
            showMovie={true}
            showEvents={false}
          />
        </div>
      </div>

      <Toast
        show={show}
        setShow={setShow}
        title="Texto copiado con éxito"
      ></Toast>
    </div>
  );
}

export async function getServerSideProps(context) {
  try {
    const { id: slug } = context.params;

    let query = qs.stringify({
      populate: {
        temas: "*",
        iniciativas: "*",
        event_image: "*",
        sede: "*",
        ediciones_giras: "*",
        peliculas: "*",
      },
    });

    const event = await apiClient.get(`/api/events/${slug}?${query}`);

    return {
      props: {
        event: event.data.data,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}
